import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    CalendarDays, 
    Crosshair,
    MapPin, 
    MessageCircle, 
    MoveRight, 
    Send, 
    ThumbsUp,
    X,
    Trash2,
    Edit3,
    Loader2,
    Users,
    ExternalLink,
    Images,
    Target,
    Bird
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { socialService } from '../../../../services';
import StateCard from '../../../../platform/components/feedback/StateCard';
import defaultProfilePic from '../../../../assets/images/dashboard-header-profile.jpg';
import './HomeActivity.css';

const HomeActivity = ({ activities = [], currentUserId }) => {
    const [activityFilter, setActivityFilter] = useState('all');
    const [socialStats, setSocialStats] = useState({});
    const [loadingStats, setLoadingStats] = useState({});
    const [showComments, setShowComments] = useState({});
    const [comments, setComments] = useState({});
    const [newComment, setNewComment] = useState({});
    const [loadingComments, setLoadingComments] = useState({});
    const [showShareModal, setShowShareModal] = useState(null);
    const [showLikesModal, setShowLikesModal] = useState(null);
    const [likesModalData, setLikesModalData] = useState([]);
    const [loadingLikes, setLoadingLikes] = useState(false);
    const [editingComment, setEditingComment] = useState(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [sharingPlatform, setSharingPlatform] = useState(null);
    
    // Use refs to track pending operations and prevent double clicks
    const pendingLikes = useRef(new Set());
    const pendingCommentToggle = useRef(new Set());

    // Fetch social stats for all events
    useEffect(() => {
        const fetchSocialStats = async () => {
            if (activities.length === 0) return;

            const eventIds = activities.map(e => e.eventId);
            try {
                const data = await socialService.getBatchStats(eventIds);
                setSocialStats(data);
            } catch (error) {
                console.error('Error fetching social stats:', error);
            }
        };

        fetchSocialStats();
    }, [activities]);

    // Toggle like with debounce protection
    const handleLike = useCallback(async (eventId) => {
        // Prevent double clicks
        if (pendingLikes.current.has(eventId)) {
            return;
        }
        
        pendingLikes.current.add(eventId);
        setLoadingStats(prev => ({ ...prev, [eventId]: true }));

        try {
            const data = await socialService.toggleLike(eventId);
            setSocialStats(prev => ({
                ...prev,
                [eventId]: {
                    ...prev[eventId],
                    totalLikes: data.totalLikes,
                    hasUserLiked: data.isLiked
                }
            }));
        } catch (error) {
            console.error('Error toggling like:', error);
        } finally {
            setLoadingStats(prev => ({ ...prev, [eventId]: false }));
            pendingLikes.current.delete(eventId);
        }
    }, []);

    // Fetch who liked an event
    const fetchLikes = useCallback(async (eventId) => {
        setLoadingLikes(true);
        try {
            const data = await socialService.getLikes(eventId);
            setLikesModalData(data);
        } catch (error) {
            console.error('Error fetching likes:', error);
            setLikesModalData([]);
        } finally {
            setLoadingLikes(false);
        }
    }, []);

    // Show likes modal
    const handleShowLikes = useCallback((eventId) => {
        setShowLikesModal(eventId);
        fetchLikes(eventId);
    }, [fetchLikes]);

    // Fetch comments for an event
    const fetchComments = useCallback(async (eventId) => {
        setLoadingComments(prev => ({ ...prev, [eventId]: true }));

        try {
            const data = await socialService.getComments(eventId);
            setComments(prev => ({ ...prev, [eventId]: data }));
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setLoadingComments(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    // Toggle comments visibility with debounce protection
    const handleToggleComments = useCallback((eventId) => {
        // Prevent rapid toggling
        if (pendingCommentToggle.current.has(eventId)) {
            return;
        }
        
        pendingCommentToggle.current.add(eventId);
        
        setShowComments(prev => {
            const newState = { ...prev, [eventId]: !prev[eventId] };
            if (newState[eventId] && !comments[eventId]) {
                fetchComments(eventId);
            }
            return newState;
        });
        
        // Release lock after a short delay
        setTimeout(() => {
            pendingCommentToggle.current.delete(eventId);
        }, 300);
    }, [comments, fetchComments]);

    // Add comment
    const handleAddComment = useCallback(async (eventId) => {
        const content = newComment[eventId]?.trim();
        if (!content) return;

        setLoadingComments(prev => ({ ...prev, [`add_${eventId}`]: true }));

        try {
            const data = await socialService.addComment(eventId, content);
            if (data.success) {
                setComments(prev => ({
                    ...prev,
                    [eventId]: [data.comment, ...(prev[eventId] || [])]
                }));
                setNewComment(prev => ({ ...prev, [eventId]: '' }));
                setSocialStats(prev => ({
                    ...prev,
                    [eventId]: {
                        ...prev[eventId],
                        totalComments: (prev[eventId]?.totalComments || 0) + 1
                    }
                }));
            }
        } catch (error) {
            console.error('Error adding comment:', error);
        } finally {
            setLoadingComments(prev => ({ ...prev, [`add_${eventId}`]: false }));
        }
    }, [newComment]);

    // Edit comment
    const handleEditComment = useCallback(async (commentId, eventId) => {
        if (!editCommentText.trim()) return;

        try {
            const data = await socialService.updateComment(commentId, editCommentText);
            if (data.success) {
                setComments(prev => ({
                    ...prev,
                    [eventId]: prev[eventId].map(c =>
                        c.commentId === commentId ? data.comment : c
                    )
                }));
                setEditingComment(null);
                setEditCommentText('');
            }
        } catch (error) {
            console.error('Error editing comment:', error);
        }
    }, [editCommentText]);

    // Delete comment
    const handleDeleteComment = useCallback(async (commentId, eventId) => {
        if (!window.confirm('Are you sure you want to delete this comment?')) return;

        try {
            const data = await socialService.deleteComment(commentId);
            if (data.success) {
                setComments(prev => ({
                    ...prev,
                    [eventId]: prev[eventId].filter(c => c.commentId !== commentId)
                }));
                setSocialStats(prev => ({
                    ...prev,
                    [eventId]: {
                        ...prev[eventId],
                        totalComments: Math.max(0, (prev[eventId]?.totalComments || 0) - 1)
                    }
                }));
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    }, []);

    // Share event - actually opens the share URL
    const handleShare = useCallback(async (eventId, platform) => {
        setSharingPlatform(platform);
        
        try {
            const data = await socialService.shareEvent(eventId, platform);
            if (data.success) {
                setSocialStats(prev => ({
                    ...prev,
                    [eventId]: {
                        ...prev[eventId],
                        totalShares: data.totalShares
                    }
                }));

                if (platform === 'CopyLink') {
                    try {
                        await navigator.clipboard.writeText(data.shareUrl);
                        alert('Link copied to clipboard!');
                    } catch (clipboardError) {
                        const textArea = document.createElement('textarea');
                        textArea.value = data.shareUrl;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert('Link copied to clipboard!');
                    }
                } else if (platform === 'Instagram') {
                    try {
                        await navigator.clipboard.writeText(data.shareUrl);
                        alert('Link copied! Open Instagram and paste it in your story or post.');
                    } catch (clipboardError) {
                        alert('Please copy this link manually: ' + data.shareUrl);
                    }
                } else {
                    const shareWindow = window.open(
                        data.shareUrl,
                        '_blank',
                        'width=600,height=500,scrollbars=yes,resizable=yes'
                    );

                    if (shareWindow) {
                        shareWindow.focus();
                    } else {
                        window.location.href = data.shareUrl;
                    }
                }
            }
        } catch (error) {
            console.error('Error sharing event:', error);
            alert('Failed to share. Please try again.');
        } finally {
            setSharingPlatform(null);
            setShowShareModal(null);
        }
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '';

        if (typeof dateString === 'string') {
            const trimmedValue = dateString.trim();

            if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedValue)) {
                return trimmedValue;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
                const [year, month, day] = trimmedValue.split('-').map(Number);
                const localDate = new Date(year, month - 1, day);
                if (!Number.isNaN(localDate.getTime())) {
                    return localDate.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                }
            }
        }

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'Date TBC';
        return date.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    };

    const resolveEventType = (eventType) => {
        const normalized = String(eventType || '').trim().toLowerCase();
        if (normalized === 'rifle') return 'Rifle';
        if (normalized === 'game') return 'Game';
        return 'Clay';
    };

    const getEventTypeIcon = (eventType) => {
        switch (eventType) {
            case 'Rifle':
                return <Target size={13} />;
            case 'Game':
                return <Bird size={13} />;
            default:
                return <Crosshair size={13} />;
        }
    };

    const getEventTypeClass = (eventType) => {
        switch (eventType) {
            case 'Rifle':
                return 'home-activity-event-type-rifle';
            case 'Game':
                return 'home-activity-event-type-game';
            default:
                return 'home-activity-event-type-clay';
        }
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(dateString);
    };

    const filteredActivities = useMemo(() => {
        switch (activityFilter) {
            case 'mine':
                return activities.filter((activity) => activity.isOwner);
            case 'friends':
                return activities.filter((activity) =>
                    !activity.isOwner ||
                    activity.shooters.some((shooter) => shooter.appUserId && shooter.appUserId !== currentUserId));
            case 'venues':
                return activities.filter((activity) => Boolean(activity.clubId) || Boolean(activity.clubName && activity.clubName !== 'Unknown'));
            default:
                return activities;
        }
    }, [activities, activityFilter, currentUserId]);

    const emptyState = useMemo(() => {
        switch (activityFilter) {
            case 'mine':
                return {
                    title: 'No personal activity yet',
                    description: 'Your own scored events will show here once you create or score one.',
                    actionLabel: 'Score an Event',
                    actionTo: '/dashboard/score'
                };
            case 'friends':
                return {
                    title: 'No friend activity yet',
                    description: 'Once your friends start shooting with you or sharing events, their activity will show up here.',
                    actionLabel: 'Open Friends',
                    actionTo: '/dashboard/friends'
                };
            case 'venues':
                return {
                    title: 'No venue activity yet',
                    description: 'Try scoring an event at a venue to start building up this view.',
                    actionLabel: 'Browse Venues',
                    actionTo: '/dashboard/clubs'
                };
            default:
                return {
                    title: 'No recent activity yet',
                    description: 'Once you start scoring events or interacting with other members, your activity feed will show up here.',
                    actionLabel: 'Score Your First Event',
                    actionTo: '/dashboard/score'
                };
        }
    }, [activityFilter]);

    if (activities.length === 0) {
        return (
            <div className="home-activity">
                <StateCard
                    icon={Users}
                    title="No recent activity yet"
                    description="Once you start scoring events or interacting with other members, your activity feed will show up here."
                    actionLabel="Score Your First Event"
                    actionTo="/dashboard/score"
                />
            </div>
        );
    }

    return (
        <div className="home-activity">
            <div className="home-activity-heading">
                <div>
                    <h5>Activity</h5>
                    <p className="home-activity-subtitle">Filter your feed by all activity, your own shoots, friend shoots, or venue-based events.</p>
                </div>
                <div className="dashboard-form-field">
                    <select id="activity" name="activity" value={activityFilter} onChange={(event) => setActivityFilter(event.target.value)}>
                        <option value="all">All</option>
                        <option value="friends">Friends</option>
                        <option value="venues">Venues</option>
                        <option value="mine">My activity</option>
                    </select>
                </div>
            </div>

            {filteredActivities.length === 0 ? (
                <StateCard
                    icon={Users}
                    title={emptyState.title}
                    description={emptyState.description}
                    actionLabel={emptyState.actionLabel}
                    actionTo={emptyState.actionTo}
                />
            ) : (
            <div className="row">
                {filteredActivities.map((event) => {
                    const stats = socialStats[event.eventId] || {};
                    const eventComments = comments[event.eventId] || [];
                    const isShowingComments = showComments[event.eventId];
                    const eventType = resolveEventType(event.eventType);

                    return (
                        <div key={event.eventId} className="col-lg-6 dashboard-card-container">
                            <div className={`dashboard-card home-activity-card home-activity-card-${eventType.toLowerCase()}`}>
                                <div className="home-activity-card-heading">
                                    <div className="home-activity-card-title-group">
                                        <h6>{event.name}</h6>
                                        <span className={`home-activity-event-type-badge ${getEventTypeClass(eventType)}`}>
                                            {getEventTypeIcon(eventType)}
                                            {eventType}
                                        </span>
                                        <span className={`home-activity-context-badge ${event.isOwner ? '' : 'secondary'}`}>
                                            {event.isOwner ? 'You own this' : 'Shared'}
                                        </span>
                                    </div>
                                    <Link to={`/dashboard/score?eventId=${event.eventId}`} className="home-activity-more-btn">
                                                        More
                                                        <MoveRight className='ms-2' size={17} />
                                    </Link>
                                </div>
                                <div className="home-activity-info">
                                    <h6><CalendarDays className='me-1 mb-1' size={14} /> {formatDate(event.eventDate)} <span className="home-activity-timeago">({formatTimeAgo(event.eventDate)})</span></h6>
                                    <h6><MapPin className='me-1 mb-1' size={14} /> {event.clubName}</h6>
                                    {event.ownerName && (
                                        <h6><Users className='me-1 mb-1' size={14} /> Hosted by {event.ownerName}</h6>
                                    )}
                                    {event.imageCount > 0 && (
                                        <h6><Images className='me-1 mb-1' size={14} /> {event.imageCount} photo{event.imageCount === 1 ? '' : 's'}</h6>
                                    )}
                                </div>
                                <h5 className="home-activity-shooters-heading">Shooters</h5>
                                <div className="activity-shooters-list">
                                    {event.shooters.map((shooter) => (
                                        <div className="home-activity-shooter" key={shooter.eventShooterId}>
                                            <img
                                                src={shooter.imageUrl}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = defaultProfilePic;
                                                }}
                                                alt={shooter.name}
                                            />
                                            <div className="activity-shooter-content">
                                                <h5>{shooter.name}</h5>
                                                <h6>{shooter.equipment}</h6>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Social Action Buttons */}
                                <div className="home-activity-bottom">
                                    <div className="social-btn-wrapper">
                                        <button 
                                            className={`social-btn ${stats.hasUserLiked ? 'liked' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleLike(event.eventId);
                                            }}
                                            disabled={loadingStats[event.eventId]}
                                            type="button"
                                        >
                                            {loadingStats[event.eventId] ? (
                                                <Loader2 size={14} className="spin" />
                                            ) : (
                                                <ThumbsUp size={14} fill={stats.hasUserLiked ? 'currentColor' : 'none'} />
                                            )}
                                            <span>{stats.totalLikes || 0}</span>
                                        </button>
                                        {/* Show "who liked" for post owner when there are likes */}
                                        {stats.totalLikes > 0 && (
                                            <button 
                                                className="view-likes-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleShowLikes(event.eventId);
                                                }}
                                                type="button"
                                                title="See who liked this"
                                            >
                                                <Users size={12} />
                                            </button>
                                        )}
                                    </div>
                                    <button 
                                        className={`social-btn ${isShowingComments ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleToggleComments(event.eventId);
                                        }}
                                        type="button"
                                    >
                                        <MessageCircle size={14} />
                                        <span>{stats.totalComments || 0}</span>
                                    </button>
                                    <button 
                                        className="social-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setShowShareModal(event.eventId);
                                        }}
                                        type="button"
                                    >
                                        <Send size={14} />
                                        <span>{stats.totalShares || 0}</span>
                                    </button>
                                </div>

                                {/* Comments Section */}
                                {isShowingComments && (
                                    <div className="comments-section">
                                        {/* Add Comment */}
                                        <div className="add-comment">
                                            <input
                                                type="text"
                                                placeholder="Write a comment..."
                                                value={newComment[event.eventId] || ''}
                                                onChange={(e) => setNewComment(prev => ({ 
                                                    ...prev, 
                                                    [event.eventId]: e.target.value 
                                                }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleAddComment(event.eventId);
                                                    }
                                                }}
                                            />
                                            <button 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleAddComment(event.eventId);
                                                }}
                                                disabled={loadingComments[`add_${event.eventId}`] || !newComment[event.eventId]?.trim()}
                                                type="button"
                                            >
                                                {loadingComments[`add_${event.eventId}`] ? (
                                                    <Loader2 size={16} className="spin" />
                                                ) : (
                                                    <Send size={16} />
                                                )}
                                            </button>
                                        </div>

                                        {/* Comments List */}
                                        {loadingComments[event.eventId] ? (
                                            <div className="comments-loading">
                                                <Loader2 size={20} className="spin" />
                                                <span>Loading comments...</span>
                                            </div>
                                        ) : (
                                            <div className="comments-list">
                                                {eventComments.length === 0 ? (
                                                    <p className="no-comments">No comments yet. Be the first to comment!</p>
                                                ) : (
                                                    eventComments.map((comment) => (
                                                        <div key={comment.commentId} className="comment-item">
                                                            <img 
                                                                src={comment.profilePictureUrl || defaultProfilePic} 
                                                                alt={comment.userName}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = defaultProfilePic;
                                                                }}
                                                            />
                                                            <div className="comment-content">
                                                                <div className="comment-header">
                                                                    <span className="comment-author">{comment.userName}</span>
                                                                    <span className="comment-time">
                                                                        {formatTimeAgo(comment.createdAt)}
                                                                        {comment.updatedAt && ' (edited)'}
                                                                    </span>
                                                                </div>
                                                                {editingComment === comment.commentId ? (
                                                                    <div className="edit-comment">
                                                                        <input
                                                                            type="text"
                                                                            value={editCommentText}
                                                                            onChange={(e) => setEditCommentText(e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                                    e.preventDefault();
                                                                                    handleEditComment(comment.commentId, event.eventId);
                                                                                }
                                                                                if (e.key === 'Escape') {
                                                                                    setEditingComment(null);
                                                                                    setEditCommentText('');
                                                                                }
                                                                            }}
                                                                            autoFocus
                                                                        />
                                                                        <button 
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handleEditComment(comment.commentId, event.eventId);
                                                                            }}
                                                                            type="button"
                                                                        >
                                                                            Save
                                                                        </button>
                                                                        <button 
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                setEditingComment(null);
                                                                                setEditCommentText('');
                                                                            }}
                                                                            type="button"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <p className="comment-text">{comment.content}</p>
                                                                )}
                                                            </div>
                                                            {comment.userId === currentUserId && editingComment !== comment.commentId && (
                                                                <div className="comment-actions">
                                                                    <button 
                                                                        className="comment-action-btn"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setEditingComment(comment.commentId);
                                                                            setEditCommentText(comment.content);
                                                                        }}
                                                                        title="Edit"
                                                                        type="button"
                                                                    >
                                                                        <Edit3 size={12} />
                                                                    </button>
                                                                    <button 
                                                                        className="comment-action-btn delete"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleDeleteComment(comment.commentId, event.eventId);
                                                                        }}
                                                                        title="Delete"
                                                                        type="button"
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Share Modal */}
                                {showShareModal === event.eventId && (
                                    <div 
                                        className="share-modal-overlay" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowShareModal(null);
                                        }}
                                    >
                                        <div 
                                            className="share-modal" 
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="share-modal-header">
                                                <h6>Share Event</h6>
                                                <button 
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowShareModal(null);
                                                    }}
                                                    type="button"
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>
                                            <div className="share-modal-content">
                                                <button 
                                                    className="share-option facebook"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleShare(event.eventId, 'Facebook');
                                                    }}
                                                    disabled={sharingPlatform !== null}
                                                    type="button"
                                                >
                                                    {sharingPlatform === 'Facebook' ? (
                                                        <Loader2 size={24} className="spin" />
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                                        </svg>
                                                    )}
                                                    <span>Facebook</span>
                                                    <ExternalLink size={14} className="external-icon" />
                                                </button>
                                                <button 
                                                    className="share-option twitter"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleShare(event.eventId, 'X');
                                                    }}
                                                    disabled={sharingPlatform !== null}
                                                    type="button"
                                                >
                                                    {sharingPlatform === 'X' ? (
                                                        <Loader2 size={24} className="spin" />
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                                        </svg>
                                                    )}
                                                    <span>X (Twitter)</span>
                                                    <ExternalLink size={14} className="external-icon" />
                                                </button>
                                                <button 
                                                    className="share-option whatsapp"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleShare(event.eventId, 'WhatsApp');
                                                    }}
                                                    disabled={sharingPlatform !== null}
                                                    type="button"
                                                >
                                                    {sharingPlatform === 'WhatsApp' ? (
                                                        <Loader2 size={24} className="spin" />
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                        </svg>
                                                    )}
                                                    <span>WhatsApp</span>
                                                    <ExternalLink size={14} className="external-icon" />
                                                </button>
                                                <button 
                                                    className="share-option instagram"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleShare(event.eventId, 'Instagram');
                                                    }}
                                                    disabled={sharingPlatform !== null}
                                                    type="button"
                                                >
                                                    {sharingPlatform === 'Instagram' ? (
                                                        <Loader2 size={24} className="spin" />
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                                        </svg>
                                                    )}
                                                    <span>Instagram</span>
                                                    <span className="share-note">(copies link)</span>
                                                </button>
                                                <button 
                                                    className="share-option copy-link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleShare(event.eventId, 'CopyLink');
                                                    }}
                                                    disabled={sharingPlatform !== null}
                                                    type="button"
                                                >
                                                    {sharingPlatform === 'CopyLink' ? (
                                                        <Loader2 size={24} className="spin" />
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                                                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                                                        </svg>
                                                    )}
                                                    <span>Copy Link</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Likes Modal - shows who liked the post */}
                                {showLikesModal === event.eventId && (
                                    <div 
                                        className="share-modal-overlay" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowLikesModal(null);
                                            setLikesModalData([]);
                                        }}
                                    >
                                        <div 
                                            className="share-modal likes-modal" 
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="share-modal-header">
                                                <h6>
                                                    <ThumbsUp size={16} className="me-2" />
                                                    Likes ({stats.totalLikes || 0})
                                                </h6>
                                                <button 
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowLikesModal(null);
                                                        setLikesModalData([]);
                                                    }}
                                                    type="button"
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>
                                            <div className="likes-modal-content">
                                                {loadingLikes ? (
                                                    <div className="likes-loading">
                                                        <Loader2 size={24} className="spin" />
                                                        <span>Loading...</span>
                                                    </div>
                                                ) : likesModalData.length === 0 ? (
                                                    <p className="no-likes">No likes yet</p>
                                                ) : (
                                                    <div className="likes-list">
                                                        {likesModalData.map((like) => (
                                                            <div key={like.likeId} className="like-item">
                                                                <img 
                                                                    src={like.profilePictureUrl || defaultProfilePic} 
                                                                    alt={like.userName}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = defaultProfilePic;
                                                                    }}
                                                                />
                                                                <div className="like-info">
                                                                    <span className="like-username">{like.userName}</span>
                                                                    <span className="like-time">{formatTimeAgo(like.createdAt)}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
};

export default HomeActivity;
